<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>
              {{ icons.mdiAccountPlus }}
            </v-icon>
            キャスト招待
          </v-card-title>
          <v-card-subtitle>
            キャストにcast_app登録用の招待コードを発行します。コードをLINE等でキャストにお渡しください。
          </v-card-subtitle>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>キャスト一覧</v-card-title>
          <v-card-text>
            <v-btn-toggle
              v-model="filter"
              mandatory
              color="primary"
              class="mb-4"
            >
              <v-btn value="all">
                すべて
              </v-btn>
              <v-btn value="uninvited">
                未招待
              </v-btn>
              <v-btn value="invited">
                招待済み
              </v-btn>
            </v-btn-toggle>

            <div
              v-if="loadingCastUsers"
              class="text-center py-4"
            >
              <v-progress-circular
                indeterminate
                color="primary"
              />
            </div>

            <v-data-table
              v-else
              :headers="castUserHeaders"
              :items="filteredCastUsers"
              :items-per-page="20"
              dense
            >
              <template #[`item.status`]="{ item }">
                <v-chip
                  v-if="item.registered"
                  small
                  color="success"
                  outlined
                >
                  登録完了
                </v-chip>
                <v-chip
                  v-else-if="item.has_active_invitation"
                  small
                  color="info"
                  outlined
                >
                  招待済み
                </v-chip>
                <v-chip
                  v-else
                  small
                  outlined
                >
                  未招待
                </v-chip>
              </template>

              <template #[`item.linkage`]="{ item }">
                <template v-if="item.club_group_size">
                  <v-tooltip
                    v-if="item.linked_clubs_count < item.club_group_size"
                    bottom
                  >
                    <template #activator="{ on }">
                      <v-chip
                        small
                        color="warning"
                        outlined
                        v-on="on"
                      >
                        <v-icon
                          small
                          left
                        >
                          {{ icons.mdiAlert }}
                        </v-icon>
                        未紐付け
                      </v-chip>
                    </template>
                    <span>グループ内の他店舗とまだ紐付けられていません。同一人物が他店舗でも勤務している場合は、group_portalで紐付けるか、同じ電話番号で登録されると複数店舗を横断して扱えます。</span>
                  </v-tooltip>
                  <v-chip
                    v-else
                    small
                    color="success"
                    outlined
                  >
                    紐付け済み
                  </v-chip>
                </template>
                <span
                  v-else
                  class="grey--text"
                >
                  —
                </span>
              </template>

              <template #[`item.actions`]="{ item }">
                <v-btn
                  small
                  color="primary"
                  :disabled="issuing"
                  @click="openConfirmDialog(item)"
                >
                  招待コード発行
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>発行済み招待一覧</v-card-title>
          <v-card-text>
            <div
              v-if="loadingInvitations"
              class="text-center py-4"
            >
              <v-progress-circular
                indeterminate
                color="primary"
              />
            </div>
            <v-data-table
              v-else
              :headers="invitationHeaders"
              :items="invitations"
              :items-per-page="20"
              dense
            >
              <template #[`item.code`]="{ item }">
                <code>{{ item.code }}</code>
              </template>
              <template #[`item.status`]="{ item }">
                <v-chip
                  v-if="item.used_at"
                  small
                  color="grey lighten-1"
                  text-color="grey darken-2"
                >
                  使用済
                </v-chip>
                <v-chip
                  v-else-if="isExpired(item)"
                  small
                  color="grey lighten-2"
                  text-color="grey darken-1"
                >
                  期限切れ
                </v-chip>
                <v-chip
                  v-else
                  small
                  color="success"
                  outlined
                >
                  有効
                </v-chip>
              </template>
              <template #[`item.created_at`]="{ item }">
                {{ formatDateTime(item.created_at) }}
              </template>
              <template #[`item.expires_at`]="{ item }">
                {{ formatDateTime(item.expires_at) }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- 発行確認ダイアログ -->
    <v-dialog
      v-model="confirmDialog"
      max-width="500"
    >
      <v-card v-if="confirmTarget">
        <v-card-title>招待コードを発行しますか？</v-card-title>
        <v-card-text>
          <p>対象キャスト: <strong>{{ confirmTarget.name }}</strong></p>
          <v-alert
            v-if="confirmTarget.club_group_size && confirmTarget.linked_clubs_count < confirmTarget.club_group_size"
            type="warning"
            outlined
            dense
            class="mt-2"
          >
            このキャストはグループ内の他店舗とまだ紐付けられていません。同一人物が他店舗でも勤務している場合は、group_portalで紐付けるか、同じ電話番号で登録されると複数店舗を横断して扱えます。
          </v-alert>
          <v-alert
            v-if="confirmTarget.has_active_invitation"
            type="info"
            outlined
            dense
            class="mt-2"
          >
            このキャストには有効な招待コードが既に存在します。再発行すると新しいコードが追加されます (古いコードも有効なまま残ります)。
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="confirmDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            :loading="issuing"
            @click="issueInvitation"
          >
            発行する
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- コード表示ダイアログ -->
    <v-dialog
      v-model="codeDialog"
      max-width="500"
      persistent
    >
      <v-card v-if="issuedInvitation">
        <v-card-title>招待コードを発行しました</v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-2">
            以下のコードまたはURLをLINE等でキャストにお渡しください。
          </p>

          <v-text-field
            :value="issuedInvitation.code"
            label="招待コード"
            readonly
            outlined
            dense
            :append-icon="icons.mdiContentCopy"
            @click:append="copyToClipboard(issuedInvitation.code, 'コード')"
          />

          <v-text-field
            :value="invitationUrl"
            label="招待URL"
            readonly
            outlined
            dense
            :append-icon="icons.mdiContentCopy"
            @click:append="copyToClipboard(invitationUrl, '招待URL')"
          />

          <p class="text-caption grey--text mt-2">
            有効期限: {{ formatDateTime(issuedInvitation.expires_at) }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            text
            @click="closeCodeDialog"
          >
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import {
  mdiAccountPlus, mdiAlert, mdiContentCopy,
} from '@mdi/js'
import CastAppInvitationApi from '@/api/admin/CastAppInvitation'

export default {
  name: 'CastAppInvitations',

  setup() {
    const castUsers = ref([])
    const invitations = ref([])
    const loadingCastUsers = ref(true)
    const loadingInvitations = ref(true)
    const filter = ref('all')

    const confirmDialog = ref(false)
    const confirmTarget = ref(null)
    const issuing = ref(false)

    const codeDialog = ref(false)
    const issuedInvitation = ref(null)

    const snackbar = ref({ show: false, message: '', color: 'success' })

    const castUserHeaders = [
      { text: '名前', value: 'name' },
      { text: '招待状態', value: 'status', sortable: false },
      { text: 'グループ内紐付け', value: 'linkage', sortable: false },
      {
        text: '', value: 'actions', sortable: false, align: 'end',
      },
    ]

    const invitationHeaders = [
      { text: 'コード', value: 'code' },
      { text: '対象キャスト', value: 'user_name' },
      { text: '状態', value: 'status', sortable: false },
      { text: '発行者', value: 'created_by_name' },
      { text: '発行日時', value: 'created_at' },
      { text: '有効期限', value: 'expires_at' },
    ]

    const filteredCastUsers = computed(() => {
      if (filter.value === 'invited') {
        return castUsers.value.filter(u => u.has_active_invitation || u.registered)
      }
      if (filter.value === 'uninvited') {
        return castUsers.value.filter(u => !u.has_active_invitation && !u.registered)
      }

      return castUsers.value
    })

    const invitationUrl = computed(() => {
      if (!issuedInvitation.value) return ''
      const host = process.env.VUE_APP_CAST_APP_HOST || ''

      return `${host}/invite/${issuedInvitation.value.code}`
    })

    const showSnackbar = (message, color = 'success') => {
      snackbar.value = { show: true, message, color }
    }

    const formatDateTime = iso => {
      if (!iso) return ''

      return new Date(iso).toLocaleString('ja-JP')
    }

    const isExpired = item => !item.used_at && new Date(item.expires_at) < new Date()

    const fetchCastUsers = async () => {
      loadingCastUsers.value = true
      try {
        const { data } = await CastAppInvitationApi.listCastUsers()
        castUsers.value = data.cast_users
      } catch (e) {
        showSnackbar('キャスト一覧の取得に失敗しました', 'error')
      } finally {
        loadingCastUsers.value = false
      }
    }

    const fetchInvitations = async () => {
      loadingInvitations.value = true
      try {
        const { data } = await CastAppInvitationApi.listInvitations()
        invitations.value = data.invitations
      } catch (e) {
        showSnackbar('招待一覧の取得に失敗しました', 'error')
      } finally {
        loadingInvitations.value = false
      }
    }

    const openConfirmDialog = item => {
      confirmTarget.value = item
      confirmDialog.value = true
    }

    const issueInvitation = async () => {
      if (!confirmTarget.value) return
      issuing.value = true
      try {
        const { data } = await CastAppInvitationApi.create(confirmTarget.value.id)
        issuedInvitation.value = data
        confirmDialog.value = false
        codeDialog.value = true
        await Promise.all([fetchCastUsers(), fetchInvitations()])
      } catch (e) {
        showSnackbar('招待コードの発行に失敗しました', 'error')
      } finally {
        issuing.value = false
      }
    }

    const closeCodeDialog = () => {
      codeDialog.value = false
      issuedInvitation.value = null
    }

    const copyToClipboard = async (text, label) => {
      try {
        await navigator.clipboard.writeText(text)
        showSnackbar(`${label}をコピーしました`)
      } catch (e) {
        showSnackbar('コピーに失敗しました', 'error')
      }
    }

    onMounted(() => {
      fetchCastUsers()
      fetchInvitations()
    })

    return {
      castUsers,
      invitations,
      loadingCastUsers,
      loadingInvitations,
      filter,
      filteredCastUsers,
      castUserHeaders,
      invitationHeaders,
      confirmDialog,
      confirmTarget,
      issuing,
      codeDialog,
      issuedInvitation,
      invitationUrl,
      snackbar,
      formatDateTime,
      isExpired,
      openConfirmDialog,
      issueInvitation,
      closeCodeDialog,
      copyToClipboard,
      icons: {
        mdiAccountPlus,
        mdiAlert,
        mdiContentCopy,
      },
    }
  },
}
</script>
